import { useMemo, useRef, useState } from "react";
import type { DemoAttendeeRow } from "./sql";
import { SIGNUP_TIMING_LABELS, collapseToCompany, companyKey, companyDisplayName } from "./helpers";
import type { ViewMode } from "./helpers";
import { FilterableTable } from "../../lib/funnel/FilterableTable";
import type { ColumnSpec } from "../../lib/funnel/FilterableTable";
import { fmtDateShort } from "../../lib/funnel/helpers";
import { downloadCsv } from "../../lib/funnel/csv";
import type { Filters } from "../../lib/funnel/filters";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

type ColumnGroup = "identity" | "outcome" | "metadata" | "leadcal" | "context";

const GROUP_LABEL: Record<ColumnGroup, string> = {
  identity: "Identity",
  outcome: "Outcome",
  metadata: "Demo metadata",
  leadcal: "Cal.com booking",
  context: "Demo context",
};

type ColDef = ColumnSpec<DemoAttendeeRow> & { group: ColumnGroup; defaultVisible: boolean };

const PLAN_TONE: Record<string, string> = { free: "b-gray", plus: "b-blue", pro: "b-violet" };

const COLUMNS: ColDef[] = [
  // Identity (default visible)
  { group: "identity", defaultVisible: true, key: "demo_date_utc", label: "Demo date",
    accessor: (r) => r.demo_date_utc, render: (_r, v) => fmtDateShort(v == null ? null : String(v)) },
  { group: "identity", defaultVisible: true, key: "organiser_email", label: "Demoer",
    filterable: true, accessor: (r) => r.organiser_email },
  { group: "identity", defaultVisible: true, key: "attendee_email", label: "Attendee email" },
  { group: "identity", defaultVisible: true, key: "user_full_name", label: "Attendee name" },
  { group: "identity", defaultVisible: true, key: "user_country_code", label: "Country",
    filterable: true, accessor: (r) => r.user_country_code },

  // Outcome (default visible)
  { group: "outcome", defaultVisible: true, key: "signup_timing_bucket", label: "Signup timing",
    filterable: true,
    render: (r) => SIGNUP_TIMING_LABELS[r.signup_timing_bucket] },
  { group: "outcome", defaultVisible: true, key: "days_demo_to_signup", label: "Days to signup", align: "right",
    render: (r) => r.days_demo_to_signup == null ? "" : Math.round(r.days_demo_to_signup) },
  { group: "outcome", defaultVisible: true, key: "activated", label: "Activated",
    filterable: true, accessor: (r) => (r.activated ? "yes" : "no"),
    render: (r) => r.activated ? <i className="ti ti-check" style={{ color: "var(--color-teal)" }} /> : <span className="muted">—</span> },
  { group: "outcome", defaultVisible: true, key: "on_paid_plan", label: "Paid",
    filterable: true, accessor: (r) => r.on_paid_plan === true ? "yes" : r.on_paid_plan === false ? "no" : "",
    render: (r) => r.on_paid_plan === true ? <i className="ti ti-check" style={{ color: "var(--color-teal)" }} /> : <span className="muted">—</span> },
  { group: "outcome", defaultVisible: true, key: "billing_plan", label: "Plan",
    filterable: true, accessor: (r) => r.billing_plan,
    render: (r) => r.billing_plan ? <span className={`badge ${PLAN_TONE[r.billing_plan] ?? "b-gray"}`}>{r.billing_plan}</span> : <span className="muted">—</span> },

  // Demo metadata (default visible)
  { group: "metadata", defaultVisible: true, key: "demo_type", label: "Demo type", filterable: true, accessor: (r) => r.demo_type },
  { group: "metadata", defaultVisible: true, key: "lead_status", label: "Lead status", filterable: true, accessor: (r) => r.lead_status },
  { group: "metadata", defaultVisible: true, key: "timeline_urgency", label: "Urgency", filterable: true, accessor: (r) => r.timeline_urgency },
  { group: "metadata", defaultVisible: true, key: "signed_up_on_call", label: "Signed up on call",
    filterable: true, accessor: (r) => boolToText(r.signed_up_on_call),
    render: (r) => boolIcon(r.signed_up_on_call) },
  { group: "metadata", defaultVisible: true, key: "calendar_connected_at_demo", label: "Calendar connected",
    filterable: true, accessor: (r) => boolToText(r.calendar_connected_at_demo),
    render: (r) => boolIcon(r.calendar_connected_at_demo) },
  { group: "metadata", defaultVisible: true, key: "adviser_count_from_demo", label: "Advisers", align: "right",
    accessor: (r) => r.adviser_count_from_demo },

  // Cal.com booking (hidden by default)
  { group: "leadcal", defaultVisible: false, key: "has_lead_cal_match", label: "Has Cal.com match",
    filterable: true, accessor: (r) => r.has_lead_cal_match ? "yes" : "no",
    render: (r) => r.has_lead_cal_match ? <i className="ti ti-check" style={{ color: "var(--color-teal)" }} /> : <span className="muted">—</span> },
  { group: "leadcal", defaultVisible: false, key: "booking_channel_bucket", label: "Channel",
    filterable: true, accessor: (r) => r.booking_channel_bucket },
  { group: "leadcal", defaultVisible: false, key: "utm_source", label: "UTM source", filterable: true, accessor: (r) => r.utm_source },
  { group: "leadcal", defaultVisible: false, key: "utm_campaign", label: "UTM campaign", filterable: true, accessor: (r) => r.utm_campaign },
  { group: "leadcal", defaultVisible: false, key: "utm_medium", label: "UTM medium", filterable: true, accessor: (r) => r.utm_medium },
  { group: "leadcal", defaultVisible: false, key: "booking_country_code", label: "Booking country",
    filterable: true, accessor: (r) => r.booking_country_code },

  // Demo context (hidden by default)
  { group: "context", defaultVisible: false, key: "competitors_mentioned", label: "Competitors",
    render: (r) => renderJsonArray(r.competitors_mentioned), accessor: (r) => stringifyJson(r.competitors_mentioned) },
  { group: "context", defaultVisible: false, key: "integration_requests", label: "Integrations",
    render: (r) => renderJsonArray(r.integration_requests), accessor: (r) => stringifyJson(r.integration_requests) },
  { group: "context", defaultVisible: false, key: "security_concerns", label: "Security concerns",
    render: (r) => renderJsonArray(r.security_concerns), accessor: (r) => stringifyJson(r.security_concerns) },
  { group: "context", defaultVisible: false, key: "pain_points", label: "Pain points",
    render: (r) => renderJsonArray(r.pain_points), accessor: (r) => stringifyJson(r.pain_points) },
  { group: "context", defaultVisible: false, key: "objections", label: "Objections",
    render: (r) => renderJsonArray(r.objections), accessor: (r) => stringifyJson(r.objections) },
  { group: "context", defaultVisible: false, key: "links", label: "External links",
    sortable: false,
    render: (r) => r.user_external_id ? (
      <span className="linklist">
        {r.logrocket_link && <a href={r.logrocket_link} target="_blank" rel="noreferrer" title="LogRocket">LR</a>}
        {r.mixpanel_link && <a href={r.mixpanel_link} target="_blank" rel="noreferrer" title="Mixpanel">MP</a>}
      </span>
    ) : <span className="muted">—</span>,
  },
];

function boolToText(v: boolean | null): string {
  if (v === true) return "yes";
  if (v === false) return "no";
  return "";
}

function boolIcon(v: boolean | null) {
  if (v === true) return <i className="ti ti-check" style={{ color: "var(--color-teal)" }} />;
  if (v === false) return <i className="ti ti-x" style={{ color: "var(--fg-3)" }} />;
  return <span className="muted">—</span>;
}

function renderJsonArray(v: unknown) {
  if (v == null) return <span className="muted">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="muted">—</span>;
    return <span style={{ fontSize: 11 }}>{v.map((x) => String(x)).join(", ")}</span>;
  }
  if (typeof v === "object") {
    return <span style={{ fontSize: 11 }}>{JSON.stringify(v)}</span>;
  }
  return <span style={{ fontSize: 11 }}>{String(v)}</span>;
}

function stringifyJson(v: unknown): string {
  if (v == null) return "";
  if (Array.isArray(v)) return v.map(String).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function DemoDrilldownTable({
  rows,
  filters,
  viewMode,
  highlightedCompanyKey = null,
  onHighlightCompany,
  pagePills,
}: {
  rows: DemoAttendeeRow[];
  filters: Filters;
  viewMode: ViewMode;
  highlightedCompanyKey?: string | null;
  onHighlightCompany?: (key: string | null) => void;
  pagePills?: FilterPillSpec[];
}) {
  const isCompany = viewMode === "company";
  const cardRef = useRef<HTMLElement | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(
    () => new Set(COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)),
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => visibleKeys.has(c.key)),
    [visibleKeys],
  );

  const toggleColumn = (key: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const distinctUsers = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.user_external_id) set.add(r.user_external_id);
    return set.size;
  }, [rows]);

  const distinctDemos = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.demo_external_id);
    return set.size;
  }, [rows]);

  // In company mode, the table spine becomes one row per prospect company
  // (rolled-up outcome flags, first-touch demo metadata). Expanding a row
  // reveals the company's attendees with per-attendee detail.
  const companyRows = useMemo(
    () => (isCompany ? collapseToCompany(rows) : []),
    [rows, isCompany],
  );

  const attendeesByCompany = useMemo(() => {
    const map = new Map<string, DemoAttendeeRow[]>();
    if (!isCompany) return map;
    for (const r of rows) {
      const k = companyKey(r);
      const arr = map.get(k);
      if (arr) arr.push(r);
      else map.set(k, [r]);
    }
    return map;
  }, [rows, isCompany]);

  const tableRows = isCompany ? companyRows : rows;

  const exportRows = useMemo(
    () => tableRows.map((r) => {
      const out: Record<string, unknown> = {};
      for (const col of COLUMNS) {
        const get = col.accessor ?? ((row: DemoAttendeeRow) => (row as unknown as Record<string, unknown>)[col.key]);
        out[col.key] = get(r);
      }
      return out;
    }),
    [tableRows],
  );

  // Company-mode column overrides: surface a Company column up front and a
  // per-row attendee count in place of attendee email/name. Identity columns
  // (organiser_email, country, etc.) are kept since they describe the demo.
  const companyOverrides = useMemo<ColDef[]>(() => {
    if (!isCompany) return [];
    const company: ColDef = {
      group: "identity", defaultVisible: true, key: "company_display_name", label: "Company",
      accessor: (r) => companyDisplayName(r),
      render: (r) => <strong>{companyDisplayName(r)}</strong>,
      filterable: false,
    };
    const attendeeCount: ColDef = {
      group: "identity", defaultVisible: true, key: "attendee_count", label: "Attendees", align: "right",
      accessor: (r) => attendeesByCompany.get(companyKey(r))?.length ?? 0,
      render: (r) => String(attendeesByCompany.get(companyKey(r))?.length ?? 0),
    };
    return [company, attendeeCount];
  }, [isCompany, attendeesByCompany]);

  const effectiveVisibleColumns = useMemo(() => {
    if (!isCompany) return visibleColumns;
    // Drop attendee email + name in company mode; lead with Company + Attendees.
    const dropped = new Set(["attendee_email", "user_full_name"]);
    const filtered = visibleColumns.filter((c) => !dropped.has(c.key));
    return [...companyOverrides, ...filtered];
  }, [isCompany, visibleColumns, companyOverrides]);

  const renderExpandedRow = (row: DemoAttendeeRow) => {
    const attendees = attendeesByCompany.get(companyKey(row)) ?? [];
    if (attendees.length === 0) {
      return <div style={{ padding: 12, color: "var(--fg-3)", fontSize: 12 }}>No attendees.</div>;
    }
    return (
      <div style={{ padding: "8px 16px 12px 40px" }}>
        <div style={{ fontSize: 11, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
          Attendees ({attendees.length})
        </div>
        <table className="t" style={{ tableLayout: "auto", width: "100%", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Name</th>
              <th style={{ textAlign: "left" }}>Email</th>
              <th style={{ textAlign: "left" }}>Demo date</th>
              <th style={{ textAlign: "left" }}>Demoer</th>
              <th style={{ textAlign: "left" }}>Signup timing</th>
              <th style={{ textAlign: "right" }}>Days to signup</th>
              <th style={{ textAlign: "left" }}>Activated</th>
              <th style={{ textAlign: "left" }}>Paid</th>
            </tr>
          </thead>
          <tbody>
            {attendees.map((a) => (
              <tr key={`${a.demo_external_id}__${a.attendee_email}`}>
                <td>{a.user_full_name ?? <span className="muted">—</span>}</td>
                <td>{a.attendee_email}</td>
                <td>{fmtDateShort(a.demo_date_utc)}</td>
                <td>{a.organiser_email ?? <span className="muted">—</span>}</td>
                <td>{SIGNUP_TIMING_LABELS[a.signup_timing_bucket]}</td>
                <td style={{ textAlign: "right" }}>{a.days_demo_to_signup == null ? "" : Math.round(a.days_demo_to_signup)}</td>
                <td>{a.activated ? <i className="ti ti-check" style={{ color: "var(--color-teal)" }} /> : <span className="muted">—</span>}</td>
                <td>{a.on_paid_plan === true ? <i className="ti ti-check" style={{ color: "var(--color-teal)" }} /> : <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const selectionPillLabel = useMemo<string | null>(() => {
    if (!isCompany || !highlightedCompanyKey) return null;
    const r = rows.find((x) => companyKey(x) === highlightedCompanyKey);
    return r ? companyDisplayName(r) : highlightedCompanyKey;
  }, [isCompany, highlightedCompanyKey, rows]);

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  if (selectionPillLabel && onHighlightCompany) {
    pills.unshift({
      kind: "selection",
      label: `Selected: ${selectionPillLabel}`,
      onClear: () => onHighlightCompany(null),
    });
  }

  const subtitle = isCompany
    ? `${companyRows.length.toLocaleString()} prospect companies · ${distinctDemos.toLocaleString()} demos · ${rows.length.toLocaleString()} attendee rows`
    : `${rows.length.toLocaleString()} attendee rows · ${distinctDemos.toLocaleString()} demos · ${distinctUsers.toLocaleString()} matched users`;

  return (
    <section className="card" ref={cardRef}>
      <SectionCardHeader
        title="Demo drilldown"
        subtitle={subtitle}
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <div style={{ position: "relative" }}>
              <button type="button" className="btn btn-sm" onClick={() => setPickerOpen((v) => !v)}>
                <i className="ti ti-columns" /> Columns
              </button>
              {pickerOpen && (
                <ColumnPicker visibleKeys={visibleKeys} onToggle={toggleColumn} onClose={() => setPickerOpen(false)} />
              )}
            </div>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => downloadCsv(exportRows, COLUMNS.map((c) => c.key), "demo-funnel", filters)}
            >
              <i className="ti ti-download" /> Download CSV
            </button>
          </div>
        }
      />
      <FilterableTable<DemoAttendeeRow>
        rows={tableRows}
        columns={effectiveVisibleColumns}
        rowKey={(r) => isCompany ? `co__${companyKey(r)}` : `${r.demo_external_id}__${r.attendee_email}`}
        searchAccessor={(r) =>
          [r.attendee_email, r.user_full_name ?? "", r.organiser_email ?? "", r.company_name_native ?? "", r.company_name_from_user ?? "", companyDisplayName(r)].join(" ")}
        searchPlaceholder={isCompany ? "Search company, demoer" : "Search attendee, demoer, company"}
        initialSortKey="demo_date_utc"
        initialSortDir="desc"
        renderExpandedRow={isCompany ? renderExpandedRow : undefined}
        highlightedRowKey={isCompany && highlightedCompanyKey ? `co__${highlightedCompanyKey}` : null}
        onRowClick={isCompany && onHighlightCompany ? (r) => {
          const k = companyKey(r);
          // The mention panels above shrink when a company is selected,
          // which pulls this card up in the document. Pin the card's
          // viewport top so the table header/panel appear fixed and the
          // clicked row drifts upward within the table rather than the
          // whole card moving.
          const beforeTop = cardRef.current?.getBoundingClientRect().top ?? 0;
          onHighlightCompany(highlightedCompanyKey === k ? null : k);
          requestAnimationFrame(() => {
            const afterTop = cardRef.current?.getBoundingClientRect().top ?? 0;
            const delta = afterTop - beforeTop;
            if (delta !== 0) window.scrollBy({ top: delta, behavior: "instant" as ScrollBehavior });
          });
        } : undefined}
      />
    </section>
  );
}

function ColumnPicker({
  visibleKeys,
  onToggle,
  onClose,
}: {
  visibleKeys: Set<string>;
  onToggle: (key: string) => void;
  onClose: () => void;
}) {
  const groups: ColumnGroup[] = ["identity", "outcome", "metadata", "leadcal", "context"];
  return (
    <div
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 30,
        background: "var(--bg-elevated, #fff)", border: "1px solid var(--border-default)",
        borderRadius: 8, padding: 12, boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
        minWidth: 240, maxHeight: 360, overflow: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <strong style={{ fontSize: 12 }}>Columns</strong>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--fg-3)" }}>
          <i className="ti ti-x" />
        </button>
      </div>
      {groups.map((g) => (
        <div key={g} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {GROUP_LABEL[g]}
          </div>
          {COLUMNS.filter((c) => c.group === g).map((c) => (
            <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "2px 0" }}>
              <input type="checkbox" checked={visibleKeys.has(c.key)} onChange={() => onToggle(c.key)} />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}
